import React from 'react';
import { useTheme } from '@mui/material';
import { useInputContext } from './InputContext';
import { Box, BoxProps } from '../../Box';
import { themePalette } from '../../../theme/theme.palette';

type StandaloneInputElementProps = {
  /**
   * Used in order to declare input element without labels
   */
  standalone?: boolean;
};
export type InputElementProps = BoxProps &
  React.InputHTMLAttributes<HTMLInputElement> &
  StandaloneInputElementProps;

const InputElement = React.forwardRef<HTMLInputElement, InputElementProps>(function InputElement(
  { readOnly, standalone, ...rest },
  ref,
) {
  const { disabled, required, invalid } = useInputContext();
  const theme = useTheme();
  const { componentTokens } = themePalette(theme.palette.mode);
  const pt = standalone ? 4 : 5;
  const pb = standalone ? 4 : 2;

  return (
    <Box
      ref={ref}
      as="input"
      width="100%"
      height="100%"
      px={4}
      pt={pt}
      pb={pb}
      position="relative"
      color={componentTokens.inputs.colors.normal.text.inputText.selected}
      fontSize="medium"
      lineHeight="normal"
      fontWeight="medium"
      backgroundColor="transparent"
      border="0"
      _placeholder={{
        opacity: standalone ? 1 : 0,
        color: componentTokens.inputs.colors.normal.text.inputText.selected,
        transition: 'opacity 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
        fontWeight: standalone ? 'normal' : 'medium',
      }}
      _focus={{
        '::placeholder': {
          opacity: 0.4,
          color: componentTokens.inputs.colors.normal.text.inputText.selected,
          fontWeight: standalone ? 'medium' : undefined,
        },
      }}
      // @ts-ignore `WebkitBoxShadow` and `WebkitTextFillColor` are not part of the TS CSS typings
      _autofill={{
        WebkitBoxShadow: `0 0 0 30px ${theme.palette['navyblue-600']} inset`,
        WebkitTextFillColor: theme.palette['white-200'],
        // @ts-ignore
        borderRadius: 'medium',
      }}
      disabled={disabled}
      aria-disabled={disabled}
      _readOnly={readOnly}
      aria-readonly={readOnly}
      required={required}
      aria-required={required}
      aria-invalid={invalid}
      {...rest}
    />
  );
});

export default InputElement;
